import { createApp, watch } from 'vue';
import { createPinia, setActivePinia } from 'pinia';
import App from './App.vue';
import { integrationService } from '@/services/integrationService';
import { useWorkspaceStore } from '@/stores/workspace';
import { useAuthStore } from '@/stores/auth';
import { analyticsService } from '@/services/analyticsService';
import { errorMonitoringService } from '@/services/errorMonitoringService';

// Import global styles
import './assets/styles/variables.css';
import './assets/styles/reset.css';
import './assets/styles/main.css';
import './assets/styles/utilities.css';

// Import router
import router from './router';

// Create and mount app
const app = createApp(App);

// Create Pinia store
const pinia = createPinia();

// Use plugins
app.use(pinia);
app.use(router);
setActivePinia(pinia);

// Initialize services
integrationService.initialize();

// Initialize auth store (Supabase session)
const authStore = useAuthStore();
const workspaceStore = useWorkspaceStore();
workspaceStore.initialize();

const workspaceState =
  workspaceStore.currentWorkspace.value ?? {
    id: 'default',
    environment: 'development',
    features: { analytics: true, errorMonitoring: true },
    name: 'Default Workspace'
  };

analyticsService.initialize({
  workspaceId: workspaceState.id,
  environment: workspaceState.environment,
  isEnabled: workspaceStore.isFeatureEnabled('analytics')
});

if (workspaceStore.isFeatureEnabled('errorMonitoring')) {
  errorMonitoringService.install();
}

router.afterEach((to) => {
  if (workspaceStore.isFeatureEnabled('analytics')) {
    analyticsService.trackNavigation(to.fullPath);
  }
});

watch(
  () => workspaceStore.currentWorkspace.value?.features.analytics ?? false,
  (enabled) => {
    analyticsService.setEnabled(enabled);
  }
);

watch(
  () => workspaceStore.currentWorkspace.value?.features.errorMonitoring ?? false,
  (enabled) => {
    if (enabled) {
      errorMonitoringService.install();
    } else {
      errorMonitoringService.uninstall();
    }
  }
);

authStore
  .initialize()
  .catch((err) => {
    console.error('Failed to initialize auth store:', err);
  })
  .finally(() => {
    app.mount('#app');
  });
